import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import SaveIcon from "@mui/icons-material/Save";
import SendIcon from "@mui/icons-material/Send";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import EquipmentMasterCatalogApi from "../../../../../infrastructure/api/equipmentMasterCatalog";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import EquipmentPublicCodeChip from "../../shared/equipmentCatalog/EquipmentPublicCodeChip";

const emptyForm = {
  id: 0,
  categoryId: "",
  brandId: "",
  manufacturerId: "",
  modelName: "",
  shortTitle: "",
  nameFa: "",
  nameEn: "",
  defaultUnit: "",
  technicalDescription: "",
};

const EquipmentMasterCatalogModify = () => {
  const { id } = useParams();
  const isEdit = !!id && id !== "create";
  const navigate = useNavigate();

  const [form, setForm] = useState(emptyForm);
  const [detail, setDetail] = useState(null);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [specDefs, setSpecDefs] = useState([]);
  const [specValues, setSpecValues] = useState({});
  const [aliasText, setAliasText] = useState("");

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const loadLookups = useCallback(async () => {
    const [c, b, m] = await Promise.all([
      EquipmentMasterCatalogApi.getCategories(false),
      EquipmentMasterCatalogApi.getBrands(false),
      EquipmentMasterCatalogApi.getManufacturers(false),
    ]);
    setCategories(c?.data || []);
    setBrands(b?.data || []);
    setManufacturers(m?.data || []);
  }, []);

  const loadItem = useCallback(async () => {
    if (!isEdit) return;
    const res = await EquipmentMasterCatalogApi.getItem(id);
    if (res.ok && res.data) {
      const d = res.data;
      setDetail(d);
      setForm({
        id: d.id,
        categoryId: d.categoryId || "",
        brandId: d.brandId || "",
        manufacturerId: d.manufacturerId || "",
        modelName: d.modelName || "",
        shortTitle: d.shortTitle || "",
        nameFa: d.nameFa || "",
        nameEn: d.nameEn || "",
        defaultUnit: d.defaultUnit || "",
        technicalDescription: d.technicalDescription || "",
      });
      const sv = {};
      (d.specValues || []).forEach((v) => {
        sv[v.specDefinitionId] = v;
      });
      setSpecValues(sv);
    }
  }, [id, isEdit]);

  useEffect(() => {
    loadLookups();
    loadItem();
  }, [loadLookups, loadItem]);

  useEffect(() => {
    if (!form.categoryId) {
      setSpecDefs([]);
      return;
    }
    EquipmentMasterCatalogApi.getSpecDefinitions(form.categoryId).then((r) =>
      setSpecDefs(r?.data || [])
    );
  }, [form.categoryId]);

  const isApproved = detail?.status === 3;

  const handleSave = async () => {
    if (!form.categoryId) return toast.warn("انتخاب دسته الزامی است.");
    if (!form.modelName) return toast.warn("مدل تجهیز الزامی است.");
    const payload = {
      ...form,
      categoryId: Number(form.categoryId),
      brandId: form.brandId ? Number(form.brandId) : null,
      manufacturerId: form.manufacturerId ? Number(form.manufacturerId) : null,
    };
    const res = isEdit
      ? await EquipmentMasterCatalogApi.updateItem(payload)
      : await EquipmentMasterCatalogApi.createItem(payload);
    if (res.ok) {
      toast.success("ذخیره شد");
      if (!isEdit && res.data?.id) navigate(`../${res.data.id}`, { replace: true });
      else loadItem();
    } else toast.error(res.message || "خطا در ذخیره");
  };

  const handleSubmit = async () => {
    const res = await EquipmentMasterCatalogApi.submitItem(Number(id));
    if (res.ok) {
      toast.success("برای بررسی ارسال شد");
      loadItem();
    } else toast.error(res.message);
  };

  const handleApprove = async () => {
    const res = await EquipmentMasterCatalogApi.approveItem(Number(id));
    if (res.ok) {
      toast.success(`تأیید شد؛ کد عمومی: ${res.data?.publicCode || ""}`);
      loadItem();
    } else toast.error(res.message);
  };

  const handleSaveSpecs = async () => {
    const values = specDefs.map((d) => {
      const v = specValues[d.id] || {};
      return {
        specDefinitionId: d.id,
        valueText: v.valueText ?? null,
        valueNumber: v.valueNumber ?? null,
        valueBoolean: v.valueBoolean ?? null,
        unit: v.unit ?? d.unit ?? null,
      };
    });
    const res = await EquipmentMasterCatalogApi.saveSpecValues({
      equipmentItemId: Number(id),
      values,
    });
    if (res.ok) {
      toast.success("مشخصات ذخیره شد");
      loadItem();
    } else toast.error(res.message);
  };

  const handleAddAlias = async () => {
    if (!aliasText.trim()) return;
    const res = await EquipmentMasterCatalogApi.addAlias({
      equipmentItemId: Number(id),
      aliasText,
      aliasType: 1,
      source: "manual",
    });
    if (res.ok) {
      toast.success("نام مستعار افزوده شد");
      setAliasText("");
      loadItem();
    } else toast.error(res.message);
  };

  const setSpecValue = (defId, patch) =>
    setSpecValues((sv) => ({ ...sv, [defId]: { ...(sv[defId] || {}), ...patch } }));

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <BreadcrumbsComponent />
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardHeader
            title={isEdit ? "ویرایش تجهیز مرجع" : "ایجاد تجهیز مرجع"}
            action={
              <Stack direction="row" spacing={1} alignItems="center">
                {detail?.publicCode && (
                  <EquipmentPublicCodeChip code={detail.publicCode} />
                )}
                {detail && (
                  <Chip size="small" label={detail.statusTitle} color="info" />
                )}
              </Stack>
            }
          />
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="دسته *"
                  value={form.categoryId}
                  disabled={isApproved}
                  onChange={(e) => setField("categoryId", e.target.value)}
                  helperText={isApproved ? "پس از تأیید غیرقابل تغییر است" : ""}
                >
                  {categories.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.titleFa} ({c.codePrefix})
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="برند"
                  value={form.brandId}
                  onChange={(e) => setField("brandId", e.target.value)}
                >
                  <MenuItem value="">—</MenuItem>
                  {brands.map((b) => (
                    <MenuItem key={b.id} value={b.id}>
                      {b.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="تولیدکننده"
                  value={form.manufacturerId}
                  onChange={(e) => setField("manufacturerId", e.target.value)}
                >
                  <MenuItem value="">—</MenuItem>
                  {manufacturers.map((m) => (
                    <MenuItem key={m.id} value={m.id}>
                      {m.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="مدل *"
                  value={form.modelName}
                  onChange={(e) => setField("modelName", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="عنوان کوتاه *"
                  value={form.shortTitle}
                  onChange={(e) => setField("shortTitle", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="واحد پیش‌فرض"
                  value={form.defaultUnit}
                  onChange={(e) => setField("defaultUnit", e.target.value)}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="نام فارسی"
                  value={form.nameFa}
                  onChange={(e) => setField("nameFa", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="نام انگلیسی"
                  value={form.nameEn}
                  onChange={(e) => setField("nameEn", e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  minRows={2}
                  label="توضیحات فنی"
                  value={form.technicalDescription}
                  onChange={(e) => setField("technicalDescription", e.target.value)}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />
            <Stack direction="row" spacing={1}>
              <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave}>
                ذخیره
              </Button>
              {isEdit && (detail?.status === 1 || detail?.status === 4) && (
                <Button variant="outlined" startIcon={<SendIcon />} onClick={handleSubmit}>
                  ارسال برای بررسی
                </Button>
              )}
              {isEdit && !isApproved && (
                <Button
                  variant="outlined"
                  color="success"
                  startIcon={<CheckCircleIcon />}
                  onClick={handleApprove}
                >
                  تأیید و صدور کد
                </Button>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      {isEdit && specDefs.length > 0 && (
        <Grid item xs={12}>
          <Card>
            <CardHeader title="مشخصات فنی" />
            <CardContent>
              <Grid container spacing={2}>
                {specDefs.map((d) => (
                  <Grid item xs={12} md={4} key={d.id}>
                    {d.dataType === 2 ? (
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label={`${d.specTitleFa}${d.unit ? ` (${d.unit})` : ""}`}
                        value={specValues[d.id]?.valueNumber ?? ""}
                        onChange={(e) =>
                          setSpecValue(d.id, {
                            valueNumber: e.target.value === "" ? null : Number(e.target.value),
                          })
                        }
                      />
                    ) : d.dataType === 3 ? (
                      <TextField
                        select
                        fullWidth
                        size="small"
                        label={d.specTitleFa}
                        value={
                          specValues[d.id]?.valueBoolean === true
                            ? "true"
                            : specValues[d.id]?.valueBoolean === false
                            ? "false"
                            : ""
                        }
                        onChange={(e) =>
                          setSpecValue(d.id, {
                            valueBoolean:
                              e.target.value === "" ? null : e.target.value === "true",
                          })
                        }
                      >
                        <MenuItem value="">—</MenuItem>
                        <MenuItem value="true">بله</MenuItem>
                        <MenuItem value="false">خیر</MenuItem>
                      </TextField>
                    ) : (
                      <TextField
                        fullWidth
                        size="small"
                        label={d.specTitleFa}
                        value={specValues[d.id]?.valueText ?? ""}
                        onChange={(e) => setSpecValue(d.id, { valueText: e.target.value })}
                      />
                    )}
                  </Grid>
                ))}
              </Grid>
              <Box sx={{ mt: 2 }}>
                <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSaveSpecs}>
                  ذخیره مشخصات
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      )}

      {isEdit && (
        <Grid item xs={12}>
          <Card>
            <CardHeader title="نام‌های مستعار / کلیدواژه جستجو" />
            <CardContent>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <TextField
                  size="small"
                  label="نام مستعار جدید"
                  value={aliasText}
                  onChange={(e) => setAliasText(e.target.value)}
                />
                <Button variant="outlined" onClick={handleAddAlias}>
                  افزودن
                </Button>
              </Stack>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {(detail?.aliases || []).map((a) => (
                  <Chip key={a.id} label={a.aliasText} size="small" />
                ))}
                {(detail?.aliases || []).length === 0 && (
                  <Typography variant="caption" color="text.secondary">
                    نام مستعاری ثبت نشده است.
                  </Typography>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      )}
    </Grid>
  );
};

export default EquipmentMasterCatalogModify;
